import { MoreVert } from '@mui/icons-material';
import {
  Typography,
  List,
  Paper,
  Divider,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { PickTeamPlayer, Team } from '../types';
import { DragAndDropWithoutContext } from './DragAndDrop';
import GroupPlayerItem from './GroupPlayerItem';
import { useState } from 'react';

type PickTeamTeamCardProps = {
  captain: string;
  players: PickTeamPlayer[];
  onDragPlayerEnd?: (fromIndex: number, toIndex: number, team: Team) => void;
  isDropDisabled?: boolean;
  isDone?: boolean;
};

function getDroppableStyle(isDroppingOver: boolean) {
  return {
    height: '100%',
    background: isDroppingOver ? '#81c784' : 'inherit',
  };
}

export default function PickTeamTeamCard({
  captain,
  players,
  isDropDisabled,
  isDone,
}: Readonly<PickTeamTeamCardProps>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleCopy = () => {
    handleCloseMenu();
    navigator.clipboard.writeText(
      players.map((player) => player.name).join('\n')
    );
  };

  const backgroundColor = isDropDisabled
    ? '#bdbdbd'
    : !isDone
      ? '#fff9c4'
      : 'inherit';

  return (
    <Paper
      elevation={4}
      sx={{
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        mb: {
          xs: 3,
          sm: 3,
          md: 3,
          lg: 0,
          xl: 0,
        },
        minWidth: {
          lg: '320px',
        },
        backgroundColor,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          variant="button"
          sx={{ fontSize: 18, textTransform: 'none' }}
        >
          Team: {captain}
        </Typography>
        <IconButton onClick={handleOpenMenu}>
          <MoreVert />
        </IconButton>
      </Stack>
      <Divider sx={{ mt: 1 }} />
      <List
        style={{ marginLeft: '-16px', marginRight: '-16px', height: '100%' }}
      >
        <DragAndDropWithoutContext<PickTeamPlayer>
          isDropDisabled={isDropDisabled}
          droppableId={`team:${captain}`}
          draggableItems={players}
          getDraggableId={(player) => player.name}
          getDroppableStyle={getDroppableStyle}
          itemRenderer={(item, isDragging, dragHandleProps) => (
            <GroupPlayerItem
              player={item}
              dragHandleProps={dragHandleProps}
              isDragging={isDragging}
              showAppearances
            />
          )}
        />
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={handleCopy}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy</ListItemText>
          </MenuItem>
        </Menu>
      </List>
    </Paper>
  );
}
